/**
 * "이 지역에서 검색"·명소 선택 뒤의 경로 이름(09-28).
 *
 * searchThisArea 전이는 범위(bounds)만 바꾸고 slug·scope·country 는 직전 검색 것을 그대로
 * 둔다(search-state 는 지명 사전을 모르는 순수 모듈). 그래서 서울에서 도쿄로 지도를 옮겨
 * 검색해도 주소가 `/s/seoul?scope=district` 로 남아 검색바는 "지도 속 이 동네"인데 주소·제목은
 * "서울"이었다. 범위 중심에서 가까운 서비스 도시로 이름을 다시 붙인다. 결과는 그대로다 —
 * 서버는 bounds 가 있으면 bounds 로 찾는다.
 */
import { CITIES, type City } from "@/config/locations";
import { KEYWORD_SLUG, citySlug } from "@/lib/search/place-slug";
import type { SearchBounds, SearchUrlState } from "@/lib/search/search-state";

/** 이 거리(km) 안에 서비스 도시가 없으면 도시 이름을 붙이지 않는다 */
const AREA_CITY_MAX_KM = 80;

function km(aLat: number, aLng: number, bLat: number, bLng: number) {
  const R = 6371;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

export function cityForBounds(b: SearchBounds): City | null {
  const lat = (b.ne_lat + b.sw_lat) / 2;
  const lng = (b.ne_lng + b.sw_lng) / 2;
  let best: { city: City; d: number } | null = null;
  for (const city of CITIES) {
    if (!city.coordinates) continue;
    const d = km(lat, lng, city.coordinates.latitude, city.coordinates.longitude);
    if (d <= AREA_CITY_MAX_KM && (!best || d < best.d)) best = { city, d };
  }
  return best?.city ?? null;
}

/** 지도 영역 검색 상태의 경로 이름을 범위에 맞춘다. 범위가 없으면 그대로. */
export function withAreaIdentity(state: SearchUrlState): SearchUrlState {
  const bounds = state.identity.bounds;
  if (!bounds) return state;
  const city = cityForBounds(bounds);
  // 지역(admin1) 코드는 지역 주소에서만 뜻이 있다 — 범위로 경로를 다시 잡으면 뗀다.
  const { region: _region, ...identity } = state.identity;
  void _region;
  return {
    ...state,
    identity: city
      ? { ...identity, slug: citySlug(city), scope: "city", country: city.countryCode }
      : { ...identity, slug: KEYWORD_SLUG, scope: "keyword", country: undefined },
  };
}
